import { useEffect, useRef, useCallback } from 'react';
import { App as CapacitorApp } from '@capacitor/app';
import { isPlatform } from '../utilities/platform';
import { startSession, endSession } from '../stores/Stats';
import { declareStorageDomain } from '../utilities/storage/storageGate';

interface SessionData {
    sessionId: string;
    startTime: number;
    isActive: boolean;
}

const SESSION_STORAGE_KEY = 'app_session';

const storage = declareStorageDomain({
    name: 'app-session',
    onLogout: (s) => s.remove(SESSION_STORAGE_KEY),
    // Bascule act-as : purge — sinon l'identité suivante hériterait du temps d'activité
    // écoulé de la précédente (piège 5.3 du plan sessions-accompagnées).
    onIdentitySwitch: (s) => s.remove(SESSION_STORAGE_KEY)
});

export const useAppSession = (userToken?: string) => {
    const sessionRef = useRef<SessionData | null>(null);
    const isInitializedRef = useRef(false);

    // Retrieve session from storage
    const getStoredSession = useCallback(async (): Promise<SessionData | null> => {
        try {
            return await storage.getJSON<SessionData>(SESSION_STORAGE_KEY);
        } catch {
            return null;
        }
    }, []);

    // Save session to storage
    const saveSession = useCallback(async (session: SessionData | null) => {
        try {
            if (session) {
                await storage.setJSON(SESSION_STORAGE_KEY, session);
            } else {
                await storage.remove(SESSION_STORAGE_KEY);
            }
        } catch {
            // Ignore storage errors
        }
    }, []);

    // Start a new session
    const startNewSession = useCallback(async () => {
        if (!userToken) return;

        try {
            const response = await startSession();
            const sessionData: SessionData = {
                sessionId: response.sessionId,
                startTime: Date.now(),
                isActive: true
            };

            sessionRef.current = sessionData;
            await saveSession(sessionData);
        } catch (error) {
            console.error('Failed to start session:', error);
        }
    }, [userToken, saveSession]);

    // End the current session
    const endCurrentSession = useCallback(
        async (useKeepalive = false): Promise<void> => {
            const session = sessionRef.current;
            if (!session || !userToken) return;
            // Taken before the request: beforeLogout, beforeunload and visibilitychange may end it
            // concurrently, and the first response may come after the page has started unloading.
            sessionRef.current = null;
            await saveSession(null);
            try {
                const duration = Math.floor((Date.now() - session.startTime) / 1000);
                await endSession(session.sessionId, duration, useKeepalive);
            } catch (error) {
                console.error('Failed to end session:', error);
            }
        },
        [userToken, saveSession]
    );

    // Handle Capacitor events (mobile)
    const setupCapacitorListeners = useCallback(() => {
        if (!isPlatform('hybrid')) return undefined;

        const handles = [
            // App goes to background
            CapacitorApp.addListener('pause', async () => endCurrentSession()),
            // App comes back to foreground
            CapacitorApp.addListener('resume', async () => startNewSession())
        ];

        // Own listeners only: removeAllListeners() would also drop the other App listeners
        // (appUrlOpen deeplinks — Ximi SSO callback — back button…).
        return (): void => {
            handles.forEach((handle) => handle.then((listener) => listener.remove()));
        };
    }, [endCurrentSession, startNewSession]);

    // Handle browser events (web)
    const setupWebListeners = useCallback(() => {
        if (isPlatform('hybrid')) return;

        const handleVisibilityChange = async () => {
            if (document.hidden) {
                // Page becomes invisible — no keepalive here because the page
                // is still alive and we need the axios interceptor for token refresh
                await endCurrentSession();
            } else {
                // Page becomes visible again
                await startNewSession();
            }
        };

        const handleBeforeUnload = () => endCurrentSession(true);

        document.addEventListener('visibilitychange', handleVisibilityChange);
        window.addEventListener('beforeunload', handleBeforeUnload);

        // Cleanup function
        return () => {
            document.removeEventListener('visibilitychange', handleVisibilityChange);
            window.removeEventListener('beforeunload', handleBeforeUnload);
        };
    }, [endCurrentSession, startNewSession]);

    // Initialization
    useEffect(() => {
        if (!userToken) {
            // Signed out (logout sets the token to ''): the next sign-in opens a new session.
            // A token rotation or an act-as switch never goes through an empty token.
            isInitializedRef.current = false;
            sessionRef.current = null;
            return;
        }
        if (isInitializedRef.current) return;
        // Marked before the awaits: a token rotation while they are pending (expired access token
        // refreshed at launch) re-runs this effect, which then opened a second session.
        isInitializedRef.current = true;

        const initSession = async () => {
            // Restore existing session if it exists
            const storedSession = await getStoredSession();
            if (storedSession && storedSession.isActive) {
                // If a session was active, end it first
                try {
                    const duration = Math.floor((Date.now() - storedSession.startTime) / 1000);
                    await endSession(storedSession.sessionId, duration);
                } catch (error) {
                    console.error('Failed to end previous session:', error);
                }
            }

            // Start a new session
            await startNewSession();
        };

        initSession();
    }, [userToken, getStoredSession, startNewSession]);

    // Lifecycle listeners, removed then re-added with fresh callbacks when the token changes
    useEffect(() => {
        if (!userToken) return undefined;

        const cleanupCapacitor = setupCapacitorListeners();
        const cleanupWeb = setupWebListeners();
        // Every platform: ContextMenu dispatches it before logout() revokes the tokens
        const handleBeforeLogout = (): void => {
            endCurrentSession(true);
        };
        window.addEventListener('beforeLogout', handleBeforeLogout);

        return (): void => {
            cleanupCapacitor?.();
            cleanupWeb?.();
            window.removeEventListener('beforeLogout', handleBeforeLogout);
        };
    }, [userToken, setupCapacitorListeners, setupWebListeners, endCurrentSession]);

    // Cleanup on component unmount only: endCurrentSession changes with every token rotation,
    // and re-running this cleanup then closed the session with nothing to reopen it.
    const endCurrentSessionRef = useRef(endCurrentSession);
    useEffect(() => {
        endCurrentSessionRef.current = endCurrentSession;
    }, [endCurrentSession]);
    useEffect(() => {
        return (): void => {
            if (sessionRef.current) {
                endCurrentSessionRef.current();
            }
        };
    }, []);

    return {
        currentSession: sessionRef.current,
        startSession: startNewSession,
        endSession: endCurrentSession
    };
};
